import React, { useEffect, useState, useRef } from "react";
import { Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { isSupabaseTimeout } from "@/lib/fetchWithTimeout";
import { supabaseHealth } from "@/lib/supabaseHealth";

const SUPABASE_STORAGE_KEY = `sb-${new URL(import.meta.env.VITE_SUPABASE_URL).hostname.split('.')[0]}-auth-token`;
const SAFETY_TIMEOUT_MS = 12_000;
const SLOW_THRESHOLD_MS = 5_000;

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const [isSlow, setIsSlow] = useState(false);
  const hasCheckedAuthRef = useRef(false);
  const isCheckingRef = useRef(true);

  useEffect(() => {
    let mounted = true;

    // "Taking longer than expected" indicator after 5s
    const slowTimer = setTimeout(() => {
      if (mounted) setIsSlow(true);
    }, SLOW_THRESHOLD_MS);

    // Safety timeout: if auth check hasn't resolved in 12s, force resolution
    const safetyTimer = setTimeout(() => {
      if (mounted && isCheckingRef.current) {
        logger.error('ProtectedRoute: safety timeout reached — auth check hung');
        supabaseHealth.forceCheck();
        // Try cached session as last resort
        const cached = getCachedSession();
        if (cached) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
        setIsChecking(false);
        isCheckingRef.current = false;
      }
    }, SAFETY_TIMEOUT_MS);

    // Helper to check if this is a tab return
    const isTabReturn = () => {
      const hiddenTime = sessionStorage.getItem('protected-route-tab-hidden-time');
      if (!hiddenTime) return false;
      const timeSinceHidden = Date.now() - parseInt(hiddenTime, 10);
      return timeSinceHidden < 10000; // 10 second window
    };

    const checkAuth = async () => {
      try {
        // Check if this is an OAuth callback (has tokens in URL hash)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const hasOAuthTokens = hashParams.has('access_token') || hashParams.has('error');

        if (hasOAuthTokens) {
          // Wait for Supabase to process the OAuth callback
          // The onAuthStateChange listener will handle the session update
          return;
        }

        // If this is a tab return and we've already checked auth, skip re-checking
        if (isTabReturn() && hasCheckedAuthRef.current) {
          if (mounted) {
            setIsChecking(false);
            isCheckingRef.current = false;
          }
          return;
        }

        const { data: { session } } = await supabase.auth.getSession();

        if (mounted) {
          setIsAuthenticated(!!session);
          setIsChecking(false);
          isCheckingRef.current = false;
          hasCheckedAuthRef.current = true;
        }
      } catch (error) {
        // The Supabase SDK wraps our SupabaseTimeoutError into AuthRetryableFetchError,
        // so we check the error message rather than instanceof
        if (isSupabaseTimeout(error)) {
          logger.error('ProtectedRoute: auth request timed out', error);
          supabaseHealth.forceCheck();

          // Fall back to cached session during outage
          const cached = getCachedSession();
          if (cached && mounted) {
            logger.error('ProtectedRoute: using cached session (degraded mode)');
            setIsAuthenticated(true);
            setIsChecking(false);
            isCheckingRef.current = false;
            hasCheckedAuthRef.current = true;
            return;
          }
          // No cached session — stay in loading, let SupabaseHealthProvider overlay handle it
          return;
        }

        logger.error('Auth check error:', error);
        if (mounted) {
          setIsAuthenticated(false);
          setIsChecking(false);
          isCheckingRef.current = false;
        }
      }
    };

    // Track tab visibility for this component
    const handleVisibilityChange = () => {
      if (document.hidden) {
        sessionStorage.setItem('protected-route-tab-hidden-time', Date.now().toString());
      } else {
        // Tab became visible - flag will be checked in checkAuth if needed
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted) {
        setIsAuthenticated(!!session);
        setIsChecking(false);
        isCheckingRef.current = false;
        hasCheckedAuthRef.current = true;
      }
    });

    return () => {
      mounted = false;
      clearTimeout(slowTimer);
      clearTimeout(safetyTimer);
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  if (isChecking || isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        {isSlow && (
          <p className="text-sm text-muted-foreground">Taking longer than expected...</p>
        )}
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/** Check localStorage for a cached Supabase session. */
function getCachedSession(): boolean {
  try {
    const raw = localStorage.getItem(SUPABASE_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    // Session object should have an access_token
    return !!(parsed?.access_token || parsed?.currentSession?.access_token);
  } catch {
    return false;
  }
}
